import { ChevronDown, Text, Users } from 'lucide-react'
import { useState } from 'react'

import { useParterManagedPayrollError } from 'components/hooks/payroll/usePartnerManagedPayrollError'
import { useGetEmployees } from 'components/hooks/useGetEmployees'
import { useGetPayScheduleEmployees } from 'components/hooks/useGetPayScheduleEmployees'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { PayrollPayScheduleSource } from 'utils/web_api.schemas'

import { EmployeePayScheduleForm } from './EmployeePayScheduleForm'
import { PayScheduleDetails } from './PayScheduleDetails'
import { PayScheduleDetailsForm } from './PayScheduleDetailsForm'
import { PayScheduleStatusBadge } from './PayScheduleStatusBadge'
import { type PaySchedule } from '../../types/payroll'
import { TabMenu } from '../reusable/TabMenu'

interface PayScheduleContainerProps {
  paySchedule: PaySchedule
  showEmployeeFields: boolean
}

export function PayScheduleContainer ({ paySchedule, showEmployeeFields }: PayScheduleContainerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [pagination] = useState({
    pageIndex: 1,
    pageSize: 10,
  })
  const { data: employees, isLoading: isEmployeesLoading } = useGetEmployees({ pagination, search: '' })
  const { data: payScheduleEmployees, isLoading: isPayScheduleEmployeesLoading } = useGetPayScheduleEmployees({ payScheduleId: paySchedule.id, pagination: { pageIndex: 1, pageSize: 10 }, search: '' })
  const { showPartnerManagedError } = useParterManagedPayrollError()

  const totalEmployees = employees?.pagination.total_records
  const totalPayScheduleEmployees = payScheduleEmployees?.pagination?.total_records

  const handleEditClick = () => {
    if (paySchedule.source === PayrollPayScheduleSource.partner) {
      showPartnerManagedError('Cannot edit pay schedule')
    } else {
      setIsEditing(true)
    }
  }

  const tabs = [
    {
      id: 'details',
      name: (
        <div className='flex items-center gap-2 ml-6'>
          <Text size={16} />
          Details
        </div>
      ),
      content: isEditing
        ? (
          <PayScheduleDetailsForm
            paySchedule={paySchedule}
            onSuccess={() => setIsEditing(false)}
            onDiscard={() => setIsEditing(false)}
          />
          )
        : (
          <PayScheduleDetails paySchedule={paySchedule} onEditClick={handleEditClick} />
          )
    },
    {
      id: 'employees',
      name: (
        <div className='flex items-center gap-2'>
          <Users size={16} />
          Employees
        </div>
      ),
      content: <EmployeePayScheduleForm paySchedule={paySchedule} />,
      hidden: !showEmployeeFields
    }

  ]

  return (
    <div className='border border-gray-200 rounded-2xl bg-white'>
      <div
        className='w-full p-4 flex flex-col xs:flex-row items-start justify-between hover:bg-gray-50 cursor-pointer rounded-2xl gap-2'
        onClick={() => setIsOpen(!isOpen)}
        data-testid='pay-schedule-container-button'
      >
        <div className='flex flex-col gap-2'>
          <div className='flex items-center'>
            <ChevronDown className={`h-5 w-5 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
            <span className='font-medium ml-2'>{paySchedule.name}</span>
          </div>
          {showEmployeeFields &&
            (
              <div className='flex items-center gap-1 body ml-7 text-gray-500'>
                <Users className='h-4 w-4' />
                <span>
                  {isEmployeesLoading || isPayScheduleEmployeesLoading
                    ? (
                      <LoadingSpinnerCentered height='16px' width='16px' data-testid='pay-schedule-loading-spinner' />
                      )
                    : (
                      `${totalPayScheduleEmployees} / ${totalEmployees} employees`
                      )}
                </span>
              </div>
            )}
        </div>

        <div className='flex items-end ml-6 xs:ml-0'>
          <PayScheduleStatusBadge paySchedule={paySchedule} />
        </div>
      </div>

      {isOpen && (
        <div>
          <TabMenu defaultTab='details' tabs={tabs} />
        </div>
      )}
    </div>
  )
}
